import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { useLocation } from 'react-router-dom'

import type { IContext } from './columns'
import type { FirstDataRenderedEvent, RowClickedEvent, RowSelectedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { ISwitchButton } from '~/components/Buttons/SwitchButton'
import type { IAppManagementByDeviceType } from '~/types/application-management'
import type { DeviceType } from '~/types/device-type'

import columns from './columns'
import AppSelectorTooltipOverlay from './Tooltip'

import TableWrapper from '../Tables/TableWrapper'

import AppLogo from '~/components/AppLogo'
import Button from '~/components/Buttons/Button'
import SwitchButton from '~/components/Buttons/SwitchButton'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import Tooltip from '~/components/Tooltip'
import { FASTLY_PATH } from '~/const/asset'
import { KEY_DOWN, KEY_ENTER, KEY_UP, shortcut } from '~/const/shortcut'
import { useShortcut } from '~/hooks/useShortcut'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { useUpdateUserPreferencesMutation, useUserPreferencesQuery, usersKeys } from '~/queries/users'
import { appKeyDeviceTypeToString, splitByDeviceType } from '~/utils/application'
import { removeDuplicateObjects } from '~/utils/array'
import { resize } from '~/utils/media'
import { isFocusedCellActions } from '~/utils/table'
import { displayToast } from '~/utils/toast'

import './styles.scss'

export interface IAppSelectorApplication {
  app_key: string
  default_display_name: string
  device_type: DeviceType
  icon_file_id?: string
}

interface IAppSelectorDropdownProps {
  apps: IAppSelectorApplication[]
  displayAppLogo?: boolean
  isMulti?: boolean
  onClick?: VoidFunction
}

const MAXIMUM_DISPLAY = 5

export const AppSelectorDropdown = ({
  apps,
  displayAppLogo,
  isMulti = false,
  onClick,
}: IAppSelectorDropdownProps): ReactElement => {
  return (
    <>
      {!isMulti ? (
        <div className="app-selector-dropdown-container" onClick={onClick}>
          {apps.length > 0 ? (
            <>
              {displayAppLogo ? (
                <AppLogo
                  appLogoUrl={
                    apps[0].icon_file_id ? resize(`${FASTLY_PATH}${apps[0].icon_file_id}`, 48, 48) : undefined
                  }
                  appPlatform={apps[0].device_type}
                  borderColor="#272727"
                  darkBackground
                  size="medium"
                />
              ) : null}
              <span className="app-name">{apps[0].default_display_name}</span>
            </>
          ) : (
            <span className="no-app-message">Select an MWM app</span>
          )}
          <MdKeyboardArrowDown color="#fff" size={24} />
        </div>
      ) : (
        <div className="multi-app-selector__dropdown-container" onClick={onClick}>
          <div className="multi-app-selector__app-logo">
            {apps.slice(0, MAXIMUM_DISPLAY)?.map(({ default_display_name, device_type, icon_file_id }, idx) => (
              <Tooltip
                key={idx}
                overlay={<AppSelectorTooltipOverlay deviceType={device_type} key={idx} label={default_display_name} />}
                placement="top"
                trigger="hover"
              >
                <div key={idx}>
                  <AppLogo
                    appLogoUrl={icon_file_id ? resize(`${FASTLY_PATH}${icon_file_id}`, 48, 48) : undefined}
                    appPlatform={device_type}
                    borderColor="#272727"
                    darkBackground
                    size="medium"
                  />
                </div>
              </Tooltip>
            ))}
            {apps.length > 5 ? (
              <Tooltip
                className="multi-app-selector__additional-app-logo__tooltip-container"
                overlay={apps.slice(MAXIMUM_DISPLAY).map(({ default_display_name, device_type }, idx) => (
                  <AppSelectorTooltipOverlay deviceType={device_type} key={idx} label={default_display_name} />
                ))}
                placement="top"
                trigger="hover"
              >
                <div className="multi-app-selector__additional-app-logo">+{apps.length - MAXIMUM_DISPLAY}</div>
              </Tooltip>
            ) : null}
            <MdKeyboardArrowDown color="#fff" size={24} />
          </div>
        </div>
      )}
    </>
  )
}

interface IAppSelectorProps {
  isMulti?: boolean
  onClose: VoidFunction
  selectedApps: { app_key: string; device_type: DeviceType }[]
}

const ENTRIES_NAME = 'apps'

export const AppSelector = ({
  isMulti,
  onClose,
  selectedApps: selectedAppsFromProps,
}: IAppSelectorProps): ReactElement => {
  /** Local state */

  const { pathname } = useLocation()

  const { dispatchToSearchParams } = useSearchParamsContext()

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact>(null)

  const [switchButtons, setSwitchButtons] = useState<ISwitchButton[]>([
    { content: 'Recent', id: 'recent', isActive: true },
    { content: 'Favourites', id: 'favourites', isActive: false },
    { content: 'All', id: 'all', isActive: false },
  ])

  /** Queries & Mutations */

  const { data: apps, isLoading: isAppsLoading } = useAppsManagementListQuery()

  const { data: userPreferences } = useUserPreferencesQuery()

  const { mutateAsync: updateUserPreferencesMutation } = useUpdateUserPreferencesMutation()

  const allSortedData = apps
    ?.reduce(splitByDeviceType(), [] as IAppManagementByDeviceType[])
    .sort(({ app_key: aName }, { app_key: bName }) => aName.localeCompare(bName))

  const favouriteApps = useMemo(
    () =>
      allSortedData?.filter(
        ({ app_key, device_type }) =>
          userPreferences?.favourite_apps?.apps?.find(
            (el: { app_key: string; device_type: string }) => el.app_key === app_key && el.device_type === device_type
          )
      ) || [],
    [allSortedData, userPreferences?.favourite_apps?.apps]
  )

  const mostRecentApps = useMemo(
    () =>
      userPreferences?.most_recent_apps?.apps
        .reverse()
        .slice(0, 10)
        .map((app) => allSortedData?.find((el) => app.app_key === el.app_key && app.device_type === el.device_type))
        .filter((app) => !!app?.app_key) || [],
    [allSortedData, userPreferences?.most_recent_apps?.apps]
  )

  /** Effects */

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNode((node) => {
        if (
          node.data &&
          selectedAppsFromProps.find(
            ({ app_key, device_type }) => node.data.app_key === app_key && node.data.device_type === device_type
          )
        ) {
          node.setSelected(true)
        }
      })

      gridRef.current.api.refreshCells({ force: true })
    }
  }, [selectedAppsFromProps])

  useShortcut({
    ...shortcut.navigateTable,
    fn: (code?: string) => {
      if (gridRef.current?.api && !isMulti) {
        const selectedNodes = gridRef.current.api.getSelectedNodes()
        const selectedNode = selectedNodes[0]

        switch (code) {
          case KEY_ENTER:
            if (selectedNodes.length > 0) {
              selectedNode.data && selectApplication(selectedNode.data.app_key, selectedNode.data.device_type)
            }
            break
          case KEY_DOWN:
            if (!isMulti) {
              if (selectedNodes.length > 0) {
                gridRef.current.api.forEachNode((node) => {
                  if (node.childIndex === selectedNode.childIndex + 1) {
                    node.setSelected(true)
                    return
                  }
                })
              } else {
                gridRef.current.api.forEachNode((node) => {
                  if (node.childIndex === 0) {
                    node.setSelected(true)
                  }
                })
              }
            }
            break
          case KEY_UP:
            if (!isMulti) {
              if (selectedNodes.length > 0) {
                gridRef.current.api.forEachNode((node) => {
                  if (node.childIndex === selectedNode.childIndex - 1) {
                    node.setSelected(true)
                    return
                  }
                })
              }
            }
            break
        }
      }
    },
  })

  /** Handlers */

  const handleSetAsFavourite = useCallback(
    async (data: IAppManagementByDeviceType) => {
      const { app_key: appKey, device_type: deviceType } = data
      const isFavourite = !!userPreferences?.favourite_apps?.apps.find(
        (el) => el.app_key === appKey && el.device_type === deviceType
      )

      if (userPreferences) {
        try {
          const response = await updateUserPreferencesMutation({
            ...userPreferences,
            favourite_apps: {
              apps: isFavourite
                ? userPreferences?.favourite_apps?.apps.filter(
                    (el) => !(el.app_key === appKey && el.device_type === deviceType)
                  )
                : userPreferences?.favourite_apps?.apps
                  ? [...userPreferences?.favourite_apps?.apps, { app_key: appKey, device_type: deviceType }]
                  : [{ app_key: appKey, device_type: deviceType }],
            },
          })

          queryClient.setQueryData(usersKeys.preferences(), response)
        } catch (e) {
          displayToast('App could not be added to favourites', 'error')
        }
      }
    },
    [queryClient, updateUserPreferencesMutation, userPreferences]
  )

  const handleFirstDataRendered = useCallback(
    (evt: FirstDataRenderedEvent<IAppManagementByDeviceType>) => {
      evt.api.forEachNode((node) => {
        if (
          selectedAppsFromProps.find(
            ({ app_key, device_type }) => node.data?.app_key === app_key && node.data?.device_type === device_type
          )
        ) {
          node.setSelected(true)
        }
      })
    },
    [selectedAppsFromProps]
  )

  const selectApplication = useCallback(
    async (appKey: string, deviceType: string) => {
      try {
        if (userPreferences) {
          const mostRecentApps =
            userPreferences.most_recent_apps?.apps?.filter(
              ({ app_key, device_type }) => !(app_key === appKey && device_type === deviceType)
            ) || []

          const response = await updateUserPreferencesMutation({
            ...userPreferences,
            most_recent_apps: {
              apps: removeDuplicateObjects([...mostRecentApps, { app_key: appKey, device_type: deviceType }]),
            },
          })

          queryClient.setQueryData(usersKeys.preferences(), response)

          queryClient.invalidateQueries({ queryKey: usersKeys.preferences() })

          dispatchToSearchParams({
            type: 'update',
            value: { app: appKeyDeviceTypeToString(appKey, deviceType) },
          })

          onClose()
        }
      } catch (e) {
        displayToast('Application could not be selected', 'error')
      }
    },
    [dispatchToSearchParams, onClose, queryClient, updateUserPreferencesMutation, userPreferences]
  )

  const handleRowClick = async (evt: RowClickedEvent) => {
    if (!isFocusedCellActions(evt, 'favourite') && !isFocusedCellActions(evt, 'store_url')) {
      selectApplication(evt.data.app_key, evt.data.device_type)

      if (pathname.startsWith('/analytics/ca/')) {
        dispatchToSearchParams({ type: 'reset-ca-filters' })
      }
      if (pathname.startsWith('/analytics/business-reports')) {
        dispatchToSearchParams({ type: 'reset-business-reports-filters' })
      }
      if (pathname.startsWith('/versions')) {
        dispatchToSearchParams({ type: 'update', value: { externalId: '' } })
      }
    }
  }

  const handleValidateSelection = useCallback(async () => {
    if (gridRef.current?.api) {
      try {
        const selectedApps = gridRef.current.api.getSelectedRows().map((data) => ({
          app_key: data.app_key as string,
          device_type: data.device_type as string,
        }))

        const mostRecentApps = userPreferences?.most_recent_apps?.apps?.filter((el) => !selectedApps.includes(el)) || []

        if (userPreferences?.user_id) {
          await updateUserPreferencesMutation({
            ...userPreferences,
            most_recent_apps: { apps: removeDuplicateObjects([...mostRecentApps, ...selectedApps]) },
          })
        }

        queryClient.invalidateQueries({ queryKey: usersKeys.preferences() })

        dispatchToSearchParams({
          type: 'update',
          value: {
            'apps[]': selectedApps.map(({ app_key, device_type }) => appKeyDeviceTypeToString(app_key, device_type)),
          },
        })

        onClose()
      } catch (e) {
        displayToast('Application(s) could not be selected', 'error')
      }
    }
  }, [dispatchToSearchParams, onClose, queryClient, updateUserPreferencesMutation, userPreferences])

  const handleRowSelected = useCallback(
    (evt: RowSelectedEvent<IAppManagementByDeviceType>) => {
      // NOTE: do not use destructuring on the node object otherwise ag-grid will lose
      // the this reference because of the arrow function
      evt.api.forEachNode(
        (node) =>
          node.isSelected() && node.rowIndex !== null && !isMulti && evt.api.ensureIndexVisible(node.rowIndex, 'top')
      )
    },
    [isMulti]
  )

  const handleSearchChange = useCallback(() => {
    setSwitchButtons((prev) => prev.map((button) => ({ ...button, isActive: button.id === 'all' })))
  }, [])

  const handleChangeTableSelection = (selection: string) =>
    setSwitchButtons((prev) => prev.map((button) => ({ ...button, isActive: button.id === selection })))

  const handleRowClickForSelection = useCallback((evt: RowClickedEvent<IAppManagementByDeviceType>) => {
    if (isFocusedCellActions(evt, 'favourite') || isFocusedCellActions(evt, 'store_url')) {
      evt.event?.stopPropagation()
    } else {
      evt.node.setSelected(!evt.node.isSelected())
    }
  }, [])

  /** Render */

  const currentData = useMemo(() => {
    switch (switchButtons.find(({ isActive }) => isActive)?.id) {
      case 'favourites':
        return favouriteApps
      case 'recent':
        return mostRecentApps
      case 'all':
      default:
        return allSortedData
    }
  }, [allSortedData, favouriteApps, mostRecentApps, switchButtons])

  const context: IContext = useMemo(
    () => ({
      favouriteApps: userPreferences?.favourite_apps?.apps || [],
      onSetAsFavourite: handleSetAsFavourite,
    }),
    [handleSetAsFavourite, userPreferences?.favourite_apps?.apps]
  )

  const commonProps = useMemo(
    () => ({
      buttonComponent: <SwitchButton buttons={switchButtons} onButtonSelectionChange={handleChangeTableSelection} />,
      columnDefs: columns,
      context,
      entriesName: ENTRIES_NAME,
      includeHiddenColumnsInQuickFilter: true,
      loading: isAppsLoading,
      onFirstDataRendered: handleFirstDataRendered,
      onRowSelected: handleRowSelected,
      onSearchTextChange: handleSearchChange,
      ref: gridRef,
      rowData: currentData,
      withPagination: false,
      withSearchBar: true,
      withSearchBarAutoFocus: true,
    }),
    [context, currentData, handleFirstDataRendered, handleRowSelected, handleSearchChange, isAppsLoading, switchButtons]
  )

  return (
    <div className={classNames('app-selector-modal-container', { 'is-multi': isMulti })}>
      <ModalBody className="app-selector-modal__body">
        <div className="app-selector-modal-content">
          {isMulti ? (
            <TableWrapper
              {...commonProps}
              onRowClicked={handleRowClickForSelection}
              rowSelection={{
                mode: 'multiRow',
              }}
            />
          ) : (
            <TableWrapper {...commonProps} onRowClicked={handleRowClick} />
          )}
        </div>
      </ModalBody>
      {isMulti ? (
        <ModalFooter className="app-selector-modal__footer">
          <div className="modal-footer-content">
            <div className="app-selection-count">
              {/* Line below was commented as it awaits a fix. So far, storing the number of selected apps in a state updated on selection change causes the ServerSideTable to rerender / glitch. */}
              {/* Total selected: <span className="bold">{gridRef.current?.api?.getSelectedRows().length} apps</span> */}
            </div>
            <Button onClick={handleValidateSelection}>Validate selection</Button>
          </div>
        </ModalFooter>
      ) : null}
    </div>
  )
}

interface ModalProps extends IAppSelectorProps {
  isOpen: boolean
}

const ModalAppSelector = ({ isMulti, isOpen, onClose, selectedApps }: ModalProps): ReactElement => {
  return (
    <Modal className="app-selector-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{isMulti ? 'Select applications' : 'Select an application'}</ModalHeader>
      <AppSelector isMulti={isMulti} onClose={onClose} selectedApps={selectedApps} />
    </Modal>
  )
}

export default ModalAppSelector
